/**
 * Kael Agents API client.
 * Direct calls to the kael backend.
 * Auth via Frappe session cookies (credentials: "include").
 *
 * Context (clientId, projectId) is read from the settings store via getSettings().
 */

import { getSettings } from '@/store/settings-store'
import type { Run } from '@/types/chat'

// NEXT_PUBLIC_KAEL_API_URL is required — populated from ui/.env.development
// or ui/.env.production by Next.js at build time, based on NODE_ENV.
export const API_BASE = process.env.NEXT_PUBLIC_KAEL_API_URL!
export const FRAPPE_URL = process.env.NEXT_PUBLIC_FRAPPE_URL || 'http://localhost:8000'
export const IS_PRODUCTION = process.env.NODE_ENV === 'production'

// ─── Hardcoded agent list (no /api/agents endpoint needed) ───────────────────

export interface KaelAgent {
  id: string
  name: string
  description: string
  type: 'agent' | 'team'
  voice?: boolean   // voice-interactive agent (selectable on /voice)
  /**
   * Grouping bucket for the entity selector. Defaults to 'kael' when unset.
   * 'experimental' agents are allow-list gated (see {@link isAgentVisible}).
   */
  group?: 'kael' | 'experimental'
}

export const KAEL_AGENTS: KaelAgent[] = [
  { id: 'comments', name: 'Comments Agent', description: 'Answer questions from project comments', type: 'agent' },
  { id: 'document_verification', name: 'Document Verification', description: 'Verify and review project documents', type: 'agent' },
  { id: 'drawings', name: 'Drawings Agent', description: 'Analyse and interpret engineering drawings', type: 'agent' },
  { id: 'form_filling', name: 'Form Filling', description: 'Assist with structured form filling tasks', type: 'agent' },
  { id: 'search', name: 'Search Agent', description: 'Knowledge base search and retrieval', type: 'agent' },
  { id: 'web_search', name: 'Web Search', description: 'Web search and research', type: 'agent' },
  { id: 'platform', name: 'Platform Agent', description: 'General platform assistance', type: 'agent' },
  { id: 'genesis', name: 'Genesis Chat', description: 'Project genesis document assistant', type: 'agent' },
  { id: 'schedule', name: 'Schedule Agent', description: 'Query and modify construction schedules', type: 'agent' },
  { id: 'virtual_site_incharge', name: 'Virtual Site Incharge', description: 'On-site execution oversight and coordination', type: 'agent', voice: true },
  { id: 'vr_safety', name: 'Virtual Safety Agent', description: 'Site safety monitoring and compliance', type: 'agent', voice: true },
  { id: 'vr_project_manager', name: 'Virtual Project Manager', description: 'Project management and delivery oversight', type: 'agent', voice: true },
  { id: 'vr_project_planner', name: 'Virtual Project Planner', description: 'Project planning and scheduling', type: 'agent', voice: true },
  { id: 'genesis_voice', name: 'Genesis (Voice)', description: 'Create the project brief and genesis documents by voice', type: 'agent', voice: true },
  { id: 'kael_team', name: 'Kael Team', description: 'Multi-agent Kael team', type: 'team' },
  { id: 'report_builder', name: 'Report Builder', description: 'Generate detailed reports from live project data — DPR, NCR, payments, schedule, vendors and more', type: 'team' },
  { id: 'kael_agent_x', name: 'Kael Agent X', description: 'Autonomous project workspace agent', type: 'agent', group: 'experimental' },
]

/**
 * Whether an agent should be shown to the current tenant.
 *
 * Only 'experimental' agents are gated; everything else is always visible.
 * Mirrors the backend's ClientConfig.is_agent_allowed so the UI never offers
 * an agent the backend would reject:
 *   - `allowed === null`  → allow-list lookup failed, fail open (show it)
 *   - `allowed` is empty  → tenant has no allow-list, all agents allowed
 *   - otherwise           → visible only if the id is explicitly allow-listed
 */
export function isAgentVisible(agent: KaelAgent, allowed: string[] | null): boolean {
  if (agent.group !== 'experimental') return true
  if (!allowed || allowed.length === 0) return true
  return allowed.includes(agent.id)
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SessionFilters {
  project_id?: string
  user_id?: string
  agent_id?: string
  limit?: number
  offset?: number
}

export interface UsageSummaryFilters {
  project_id?: string
  agent_id?: string
  user_id?: string
  start_date?: string
  end_date?: string
}

export interface GenesisRevision {
  revision: string
  project_id: string
  triggered_by: string
  status: string
  created_at: string
  output_dir: string
  /** Free-text "commit message" entered by the user when the revision was saved. */
  message?: string
}

// ─── Helper ───────────────────────────────────────────────────────────────────

/**
 * Fetch wrapper that sends auth headers.
 *
 * Auth resolution order:
 * 1. Token from localStorage (set in Context selector)
 * 2. NEXT_PUBLIC_FRAPPE_API_TOKEN env var (set at build/dev time)
 * 3. Cookie-based auth (credentials: include)
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const url = `${API_BASE}${path}`
  const token = typeof window !== 'undefined'
    ? localStorage.getItem('kael-api-token') || ''
    : ''
  const { frappeSiteName } = getSettings()

  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string> || {}),
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  headers['X-Frappe-Site-Name'] = frappeSiteName

  return fetch(url, {
    credentials: 'include',
    ...init,
    headers,
  })
}

/** Set the API token for debug UI (persisted in localStorage). */
export function setApiToken(token: string) {
  if (typeof window !== 'undefined') {
    localStorage.setItem('kael-api-token', token)
  }
}

/** Get the current API token. */
export function getApiToken(): string {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('kael-api-token') || ''
  }
  return ''
}

/**
 * Fetch for auth endpoints — always uses a relative path so requests go
 * through the Next.js proxy route (/api/auth/...) rather than directly to
 * the backend. This ensures cookies are stored same-origin (e.g. localhost:3010)
 * and sent on subsequent requests, avoiding the SameSite=Lax cross-origin
 * cookie problem.
 */
async function authFetch(subpath: string, init: RequestInit = {}): Promise<Response> {
  const { frappeSiteName } = getSettings()
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string> || {}),
    'X-Frappe-Site-Name': frappeSiteName,
  }
  return fetch(`/api/auth/${subpath}`, {
    credentials: 'include',
    ...init,
    headers,
  })
}

// ─── Auth ─────────────────────────────────────────────────────────────────────

export interface FrappeSite {
  client_id: string
  frappe_backend_url: string
  frappe_site_name: string
}

export class BackendUnreachableError extends Error {
  constructor(message = 'Backend unreachable') {
    super(message)
    this.name = 'BackendUnreachableError'
  }
}

// ─── Usage limits (SCRUM-47) ──────────────────────────────────────────────────

export type UsageWindowKind = 'five_hour' | 'weekly'

/** One allowance window's state. Tokens are internal — the UI shows `used_pct`. */
export interface UsageWindowStatus {
  kind: UsageWindowKind
  label: string
  enforced: boolean
  limit_tokens: number
  used_tokens: number
  remaining_tokens: number
  used_pct: number
  exceeded: boolean
  window_start: string
  window_seconds: number
  reset_at: string
  reset_in_seconds: number
}

/** `enforced: false` means limits are off for this caller — hide the indicator. */
export interface UsageLimitStatus {
  enforced: boolean
  allowed: boolean
  blocked_window: UsageWindowKind | null
  limits: Partial<Record<UsageWindowKind, UsageWindowStatus>>
}

/** Global metering kill switch. `source: 'env_default'` means no admin has ever flipped it. */
export interface UsageMeteringState {
  enabled: boolean
  source: 'database' | 'env_default'
  actor_user_id: string | null
  changed_at: string | null
  reason: string | null
}

/** Discriminator the backend puts in the 429 body for an allowance block. */
export const USAGE_LIMIT_ERROR = 'usage_limit_exceeded'

/**
 * Thrown when the 5-hour or weekly allowance is spent.
 *
 * Distinct from the burst limiter's 429 (which has a plain-string detail), so the
 * chat UI can show the limit banner + reset countdown instead of a retry toast.
 */
export class UsageLimitError extends Error {
  readonly window: UsageWindowKind | null
  readonly limits: Partial<Record<UsageWindowKind, UsageWindowStatus>>

  constructor(detail: {
    message?: string
    window?: UsageWindowKind | null
    limits?: Partial<Record<UsageWindowKind, UsageWindowStatus>>
  }) {
    super(detail.message || "You've reached your usage limit.")
    this.name = 'UsageLimitError'
    this.window = detail.window ?? null
    this.limits = detail.limits ?? {}
  }
}

/**
 * Throw a typed UsageLimitError if this 429 is an allowance block.
 *
 * Returns normally for any other response — including a burst-limiter 429 — so the
 * caller falls through to its own generic error.
 */
async function throwIfUsageLimited(res: Response): Promise<void> {
  if (res.status !== 429) return
  let detail: unknown
  try {
    detail = (await res.clone().json())?.detail
  } catch {
    return // not JSON (burst limiter sends a plain string) — not ours
  }
  if (detail && typeof detail === 'object' && (detail as { error?: string }).error === USAGE_LIMIT_ERROR) {
    throw new UsageLimitError(detail as ConstructorParameters<typeof UsageLimitError>[0])
  }
}

export const auth = {
  /**
   * List available Frappe sites — public, no auth required.
   * Throws {@link BackendUnreachableError} when the backend cannot be reached
   * (network failure or 5xx from the proxy) so callers can show a clear
   * "backend down" state instead of a blank list.
   */
  sites: async (): Promise<FrappeSite[]> => {
    let res: Response
    try {
      res = await authFetch('sites')
    } catch {
      throw new BackendUnreachableError()
    }
    if (res.status >= 500) throw new BackendUnreachableError()
    if (!res.ok) return []
    const data = await res.json().catch(() => ({}))
    return data.sites ?? []
  },

  /**
   * Sites this deployment offers in its login picker — public, called before a session exists.
   *
   * `sites()` above needs a session (pentest F-09 scoped it to the caller's own tenant), so it
   * returns [] on the login page. This is the pre-auth list, served from the backend's
   * clients.yaml `settings.login_sites` allowlist rather than hardcoded per environment in the
   * UI — a dev deployment answers with its own sites, prod with prod's.
   */
  loginSites: async (): Promise<FrappeSite[]> => {
    let res: Response
    try {
      res = await authFetch('login-sites')
    } catch {
      throw new BackendUnreachableError()
    }
    if (res.status >= 500) throw new BackendUnreachableError()
    if (!res.ok) return []
    const data = await res.json().catch(() => ({}))
    return data.sites ?? []
  },

  /**
   * Login via the Next.js auth proxy → Kael backend → Frappe.
   * Cookies are set same-origin (under the Next.js dev server URL) so they
   * are sent automatically on all subsequent requests.
   */
  login: async (
    username: string,
    password: string,
    opts: { frappeUrl?: string; frappeSiteName?: string } = {}
  ): Promise<{ user: string; full_name: string }> => {
    const res = await authFetch('login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username,
        password,
        frappe_url: opts.frappeUrl,
        frappe_site_name: opts.frappeSiteName,
      }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error((err as any).error || 'Login failed')
    }
    return res.json()
  },

  logout: async (): Promise<void> => {
    // Call Frappe logout directly to invalidate server-side session
    const { frappeSiteName } = getSettings()
    const sites = await auth.sites().catch(() => [] as FrappeSite[])
    const site = sites.find(s => s.frappe_site_name === frappeSiteName)
    if (site) {
      try {
        await fetch(`${site.frappe_backend_url}/api/method/logout`, {
          method: 'GET',
          credentials: 'include',
          headers: { 'X-Frappe-Site-Name': frappeSiteName },
        })
      } catch {
        // Frappe logout may fail — continue with local cleanup
      }
    }
    // Also call our backend logout to clear any proxy cookies
    await authFetch('logout', { method: 'POST' }).catch(() => {})
  },

  me: async (): Promise<{ user_id: string; full_name: string; auth_mode: string } | null> => {
    try {
      // Use apiFetch so the localStorage Bearer token is included (token auth)
      const res = await apiFetch('/api/auth/me')
      if (!res.ok) return null
      return res.json()
    } catch {
      return null
    }
  },
}

// ─── Health ───────────────────────────────────────────────────────────────────

export const health = {
  check: async (): Promise<{ status: string; version?: string }> => {
    const res = await apiFetch('/api/health')
    if (!res.ok) throw new Error('Health check failed')
    return res.json()
  },
}

// ─── Chat ─────────────────────────────────────────────────────────────────────

export interface ChatOptions {
  sessionId?: string
  projectId?: string
  files?: File[]
  uploadIds?: string[]
  signal?: AbortSignal
  /** Chat mode (flash|pro) — honoured by agents with model variants (e.g. schedule). */
  mode?: string
  /**
   * Explicit model id for this turn, from the model picker. Outranks `mode` on the server.
   * Omitted (not sent as "") when the user has picked nothing, so the agent keeps its own
   * configured model and the request is byte-identical to what it was before this feature.
   */
  model?: string
}

/** A run that is no longer streaming, as reported by GET /api/chat/reconnect. */
export interface ReconnectState {
  /** 'interrupted' | 'cancelled' | 'completed' | 'failed' | 'not_found' — the record's own status; `resumable` says which can continue. */
  status: string
  run_id: string
  session_id: string
  agent_id: string
  /** What the run had produced before it stopped. Often '' for an early death. */
  content: string
  /** False means the server knows of no way to continue it — show `message`, do not POST. */
  resumable: boolean
  /** Only sent for 'interrupted'. */
  message?: string
  /** Why the run ended early: "stopped by user" | "backend restarted" | a safe error string. */
  stop_reason?: string | null
}

export const chat = {
  sendMessage: async (
    agentId: string,
    message: string,
    opts: ChatOptions = {}
  ): Promise<ReadableStream<Uint8Array> | null> => {
    const formData = new FormData()
    formData.append('message', message)
    formData.append('stream', 'true')
    if (opts.sessionId) formData.append('session_id', opts.sessionId)
    if (opts.projectId) formData.append('project_id', opts.projectId)
    if (opts.files) {
      opts.files.forEach((file) => formData.append('files', file))
    }
    if (opts.uploadIds && opts.uploadIds.length > 0) {
      formData.append('upload_ids', opts.uploadIds.join(','))
    }
    if (opts.mode) formData.append('mode', opts.mode)
    if (opts.model) formData.append('model', opts.model)

    const res = await apiFetch(`/api/chat/${agentId}`, {
      method: 'POST',
      body: formData,
      signal: opts.signal,
    })
    if (!res.ok) {
      await throwIfUsageLimited(res)
      throw new Error(`Chat failed: ${res.status}`)
    }
    return res.body
  },

  sendTeamMessage: async (
    teamId: string,
    message: string,
    opts: ChatOptions = {}
  ): Promise<ReadableStream<Uint8Array> | null> => {
    const formData = new FormData()
    formData.append('message', message)
    formData.append('stream', 'true')
    if (opts.sessionId) formData.append('session_id', opts.sessionId)
    if (opts.projectId) formData.append('project_id', opts.projectId)
    if (opts.files) {
      opts.files.forEach((file) => formData.append('files', file))
    }
    if (opts.uploadIds && opts.uploadIds.length > 0) {
      formData.append('upload_ids', opts.uploadIds.join(','))
    }

    if (opts.mode) formData.append('mode', opts.mode)
    if (opts.model) formData.append('model', opts.model)

    const res = await apiFetch(`/api/chat/team/${teamId}`, {
      method: 'POST',
      body: formData,
      signal: opts.signal,
    })
    if (!res.ok) {
      await throwIfUsageLimited(res)
      throw new Error(`Team chat failed: ${res.status}`)
    }
    return res.body
  },

  reconnect: async (sessionId: string, runId?: string): Promise<ReadableStream<Uint8Array> | null> => {
    const params = new URLSearchParams({ session_id: sessionId })
    if (runId) params.set('run_id', runId)
    const res = await apiFetch(`/api/chat/reconnect?${params}`)
    if (!res.ok) return null
    // If response is JSON (completed/not_found), not a stream
    const contentType = res.headers.get('content-type') || ''
    if (contentType.includes('application/json')) {
      return null  // Not streaming — load from DB instead
    }
    return res.body
  },

  /**
   * State of a session's newest non-streaming run, or null while still streaming.
   *
   * Resumable run id and agent_id come from here, not the stored transcript — its
   * run id 404s on resume. `content` holds a restart-killed run's partial output,
   * unavailable anywhere else.
   */
  reconnectState: async (sessionId: string): Promise<ReconnectState | null> => {
    const res = await apiFetch(`/api/chat/reconnect?session_id=${encodeURIComponent(sessionId)}`)
    if (!res.ok) return null
    // A live run answers with an SSE stream, not JSON — that is the reconnect path's job.
    if (!(res.headers.get('content-type') || '').includes('application/json')) return null
    const data = await res.json().catch(() => null)
    return data && data.status && data.status !== 'not_found' ? (data as ReconnectState) : null
  },

  /**
   * Questions a PAUSED run is still waiting on.
   *
   * `checkActive` only knows about runs that are STREAMING (it reads an in-memory registry),
   * so a run paused on ask_user_confirmation is invisible to it — and after a backend restart
   * that registry is empty anyway. Reloading the page then showed the transcript with no way
   * to answer, and the run sat paused forever. This reads the durable server-side state.
   */
  pendingQuestions: async (
    agentId: string,
    sessionId: string,
  ): Promise<{
    run_id: string
    agent_id: string
    session_id: string
    questions: Array<{ tool_call_id: string; tool_name: string; tool_args: Record<string, unknown> }>
  } | null> => {
    try {
      const res = await apiFetch(`/api/chat/${agentId}/sessions/${sessionId}/pending-questions`)
      if (!res.ok) return null
      const data = await res.json()
      return data && Array.isArray(data.questions) && data.questions.length > 0 ? data : null
    } catch {
      return null
    }
  },

  /** Check if a session has an active streaming run. */
  checkActive: async (sessionId: string): Promise<{ status: string; run_id?: string } | null> => {
    try {
      const res = await apiFetch(`/api/chat/active?session_id=${sessionId}`)
      if (!res.ok) return null
      return res.json()
    } catch {
      return null
    }
  },

  /** Respond to a paused HITL run (ask_user_confirmation or ask_user_question). */
  confirmRun: async (
    agentId: string,
    runId: string,
    sessionId: string,
    answer: string,
    signal?: AbortSignal,
  ): Promise<ReadableStream<Uint8Array> | null> => {
    const params = new URLSearchParams({ answer, session_id: sessionId, stream: 'true' })
    const res = await apiFetch(`/api/chat/${agentId}/runs/${runId}/confirm?${params}`, {
      method: 'POST',
      signal: signal ?? undefined,
    })
    if (!res.ok) throw new Error(`confirmRun ${res.status}`)
    return res.body
  },
}

// ─── Sessions ─────────────────────────────────────────────────────────────────

/** Thrown when the pin route answers 404/405 — the write side is not deployed yet. */
export const SESSION_PIN_UNAVAILABLE = 'Pinning is not available on this server yet'

/** Query params for GET /api/sessions/{id} — omit `runs_limit` for the server default (8). */
export interface SessionGetOptions {
  runs_limit?: number
  /** run_id cursor — returns runs OLDER than this one. */
  before?: string
  raw?: boolean
}

/** GET /api/sessions/{id} response — paginated newest-runs-first by parent run id. */
export interface SessionPage {
  session_id: string
  session_name?: string
  runs: Run[]
  has_more: boolean
  next_cursor: string | null
  total_runs: number
  session_data?: Record<string, unknown>
  metrics?: unknown
  [key: string]: unknown
}

/** GET /api/sessions/{id}/runs/{run_id}/tool_calls/{tool_call_id} response. */
export interface SessionToolCallResult {
  session_id: string
  run_id: string
  tool_call_id: string
  tool_name: string
  result: string
  result_chars: number
  truncated: boolean
}

export const sessions = {
  list: async (filters: SessionFilters = {}) => {
    const params = new URLSearchParams()
    if (filters.project_id) params.set('project_id', filters.project_id)
    if (filters.user_id) params.set('user_id', filters.user_id)
    if (filters.agent_id) params.set('agent_id', filters.agent_id)
    if (filters.limit) params.set('limit', String(filters.limit))
    if (filters.offset) params.set('offset', String(filters.offset))

    const res = await apiFetch(`/api/sessions?${params}`)
    if (!res.ok) {
      if (res.status === 401 || res.status === 404) return []
      throw new Error(`Failed to fetch sessions (${res.status})`)
    }
    const data = await res.json()
    return data.sessions || data.data || []
  },

  /** GET /api/sessions/{id}, paginated by run. `opts` defaults match the server's own. */
  get: async (sessionId: string, opts: SessionGetOptions = {}): Promise<SessionPage | null> => {
    const params = new URLSearchParams()
    if (opts.runs_limit != null) params.set('runs_limit', String(opts.runs_limit))
    if (opts.before) params.set('before', opts.before)
    if (opts.raw) params.set('raw', '1')
    const qs = params.toString()
    const res = await apiFetch(`/api/sessions/${sessionId}${qs ? `?${qs}` : ''}`)
    if (!res.ok) {
      if (res.status === 404) return null
      throw new Error('Failed to fetch session')
    }
    return res.json()
  },

  /** Full result for one tool call — the expand behind a truncated result_preview. */
  toolCallResult: async (sessionId: string, runId: string, toolCallId: string): Promise<SessionToolCallResult> => {
    const res = await apiFetch(`/api/sessions/${sessionId}/runs/${runId}/tool_calls/${toolCallId}`)
    if (!res.ok) throw new Error(`Failed to fetch tool call result (${res.status})`)
    return res.json()
  },

  delete: async (sessionId: string) => {
    const res = await apiFetch(`/api/sessions/${sessionId}`, { method: 'DELETE' })
    return res.ok
  },

  /**
   * Pin a chat, optionally filing it under a folder ('' = no folder).
   *
   * ⚠ NOT DEPLOYED YET — a 404/405 here means "not shipped", not "failed";
   * {@link SESSION_PIN_UNAVAILABLE} is what the caller shows.
   */
  setPin: async (sessionId: string, pinned: boolean, folder = ''): Promise<void> => {
    const res = await apiFetch(`/api/sessions/${encodeURIComponent(sessionId)}/pin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pinned, folder }),
    })
    if (res.status === 404 || res.status === 405) throw new Error(SESSION_PIN_UNAVAILABLE)
    if (!res.ok) throw new Error(`Failed to pin session (${res.status})`)
  },

  /**
   * Sessions for the Traces list.
   *
   * ⚠ NOT `sessions.list`. That helper returns [] on a 401, so an expired or wrong-site session
   * renders as "No sessions yet" — the screen states, as fact, that a user has no history when
   * the truth is that we could not ask. Observed live: /api/sessions?limit=100 returned 200 and
   * then 401 twice, and the page showed an empty list all three times.
   */
  /** anyUser: every owner in the tenant, not just the caller — for the trace view. */
  listOrThrow: async (limit = 100, anyUser = false): Promise<unknown[]> => {
    const res = await apiFetch(
      `/api/sessions?limit=${limit}${anyUser ? '&any_user=true' : ''}`,
    )
    if (res.status === 401) throw new Error('Your session has expired — sign in again.')
    if (!res.ok) throw new Error(`Failed to fetch sessions (${res.status})`)
    const data = await res.json()
    return data.sessions || data.data || []
  },

  /** Tool spans for one session, shaped for a waterfall. Results are NOT included. */
  trace: async (sessionId: string): Promise<SessionTrace | null> => {
    const res = await apiFetch(`/api/sessions/${sessionId}/trace`)
    if (!res.ok) {
      if (res.status === 404) return null
      throw new Error(`Failed to fetch trace (${res.status})`)
    }
    return res.json()
  },

  /** Aggregate stats for one chat — tokens, cost, context-window use, turns, duration. */
  stats: async (sessionId: string): Promise<SessionStats | null> => {
    const res = await apiFetch(`/api/sessions/${sessionId}/stats`)
    if (!res.ok) {
      if (res.status === 404) return null
      throw new Error(`Failed to fetch session stats (${res.status})`)
    }
    return res.json()
  },

  /**
   * Manual whole-history compaction — the same summary + kept-tail the automatic
   * compaction hook runs, triggered on demand. Throws with the backend's detail message
   * on 409 (a run is streaming or paused) or any other failure.
   */
  compact: async (sessionId: string): Promise<SessionCompactResult> => {
    const res = await apiFetch(`/api/sessions/${encodeURIComponent(sessionId)}/compact`, {
      method: 'POST',
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err?.detail || `Failed to compact session (${res.status})`)
    }
    return res.json()
  },
}

/** One tool call. `offset` is seconds from the start of its OWN run, not the session. */
export interface TraceSpan {
  span_id: string
  /** 'tool' — a measured tool call. 'llm' — time in the model. */
  kind: 'tool' | 'llm'
  /**
   * Whether the timing was RECORDED or inferred. Tool spans are always exact. An LLM span is
   * exact only when the assistant message carried a duration — 5,945 of 14,742 do. The rest are
   * inferred from a whole-second `created_at`, so they are right in shape and ±1s in placement,
   * and the UI must not present them as measurement.
   */
  exact: boolean
  input_tokens?: number | null
  output_tokens?: number | null
  reasoning_tokens?: number | null
  time_to_first_token?: number | null
  run_id: string
  run_index: number
  name: string
  start: number | null
  end: number | null
  duration: number | null
  offset: number | null
  error: boolean
  /**
   * 'raised'   — the tool threw, and agno flagged it.
   * 'returned' — the tool returned {"error": ...} with no flag set. This is the COMMON kind:
   *              2,869 spans across the corpus versus 234 flagged, so a UI that colours only
   *              on the flag shows a green trace for a session where most calls failed.
   */
  error_kind: string
  error_preview: string
  args_preview: string
  result_bytes: number
  parent_span_id: string | null
  child_run_id: string | null
}

export interface TraceRun {
  run_id: string | null
  index: number
  model: string | null
  started_at: number | null
  wall_seconds: number | null
  /** Time inside tools — tool spans only. */
  tool_seconds: number
  /** Time inside the model. Reported, NOT derived as wall - tool_seconds. */
  model_seconds: number
  duration: number | null
  input_tokens: number | null
  output_tokens: number | null
  span_count: number
  /** Tool spans only — span_count also counts model spans. */
  tool_count: number
  error_count: number
}

export interface SessionTrace {
  session_id: string
  agent_id: string | null
  runs: TraceRun[]
  spans: TraceSpan[]
  started_at: number | null
  ended_at: number | null
  wall_seconds: number | null
  span_count: number
  /** Tool spans only — span_count also counts model spans. */
  tool_count: number
  error_count: number
}

/** POST /api/sessions/{id}/compact response. */
export interface SessionCompactResult {
  session_id: string
  before_tokens: number
  after_tokens: number
  summarised_runs: number
  kept_runs: number
  compacted_at: number
}

/** GET /api/sessions/{id}/stats — the info-popover contract, shared with PMC. */
export interface SessionStats {
  session_id: string
  agent_id: string | null
  model: string | null
  mode: 'flash' | 'pro' | null
  turns: number
  tokens: { input: number; output: number; cached_input: number; total: number }
  cost_usd: number
  context: {
    window: number
    last_input_tokens: number
    used_fraction: number
    /** Token count at which Kael auto-compacts. Absent on backends before that field shipped. */
    compact_at_tokens?: number
  }
  duration_seconds: { active: number; wall: number }
  first_run_at: string | null
  last_run_at: string | null
  status: string | null
  /** Why the latest run ended early, e.g. "stopped by user" | "backend restarted". */
  stop_reason?: string | null
}

// ─── Genesis ──────────────────────────────────────────────────────────────────

export type OptionalDocType = 'pmc_resource_comparison' | 'logistics_plan' | 'sustainability_report'

export const genesis = {
  start: async (
    projectId: string,
    opts?: {
      skipPmc?: boolean
      skipLogisticsPlan?: boolean
      skipSustainabilityReport?: boolean
    },
  ): Promise<{
    task_id?: string
    revision?: string
    status: string
    skip_pmc?: boolean
    skip_logistics_plan?: boolean
    skip_sustainability_report?: boolean
  }> => {
    // Defaults match backend: PMC default ON (skip=false); Logistics + Sustainability default OFF (skip=true).
    const res = await apiFetch(`/api/genesis/${projectId}/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        skip_pmc: opts?.skipPmc ?? false,
        skip_logistics_plan: opts?.skipLogisticsPlan ?? true,
        skip_sustainability_report: opts?.skipSustainabilityReport ?? true,
      }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err?.detail?.message || err?.detail || 'Failed to start genesis generation')
    }
    return res.json()
  },

  generateOptional: async (
    projectId: string,
    docType: OptionalDocType,
  ): Promise<{ status: string; doc_type: OptionalDocType; started_at: string }> => {
    const res = await apiFetch(`/api/genesis/${projectId}/generate-optional`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ doc_type: docType }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err?.detail?.message || err?.detail || `Failed to generate ${docType}`)
    }
    return res.json()
  },

  draftOptionalPdfUrl: (projectId: string, docType: OptionalDocType): string =>
    `/api/genesis/${projectId}/draft/optional-pdf/${docType}`,

  revisionOptionalPdfUrl: (projectId: string, revision: number, docType: OptionalDocType): string =>
    `/api/genesis/${projectId}/revisions/${revision}/optional-pdf/${docType}`,

  /** Per-doc PDF URL for the current draft. Works for any doc_type and for "brief". */
  draftDocPdfUrl: (projectId: string, docType: string): string =>
    docType === 'brief'
      ? `/api/genesis/${projectId}/draft/brief/pdf`
      : `/api/genesis/${projectId}/draft/${docType}/pdf`,

  /** Per-doc PDF URL for a saved revision. Works for any doc_type and for "brief". */
  revisionDocPdfUrl: (projectId: string, revision: number, docType: string): string =>
    docType === 'brief'
      ? `/api/genesis/${projectId}/revisions/${revision}/brief/pdf`
      : `/api/genesis/${projectId}/revisions/${revision}/${docType}/pdf`,

  status: async (projectId: string): Promise<{
    status: string; revision?: string; task_id?: string;
    started_at?: string; finished_at?: string; error?: string;
    documents?: Record<string, string>
  }> => {
    const res = await apiFetch(`/api/genesis/${projectId}/status`)
    if (!res.ok) throw new Error('Failed to get genesis status')
    return res.json()
  },

  revisions: async (projectId: string): Promise<GenesisRevision[]> => {
    const res = await apiFetch(`/api/genesis/${projectId}/revisions`)
    if (!res.ok) return []
    const data = await res.json()
    return data.revisions || []
  },

  document: async (projectId: string, revision: number, docType: string): Promise<{ content: string } | null> => {
    const res = await apiFetch(`/api/genesis/${projectId}/revisions/${revision}/${docType}`)
    if (!res.ok) return null
    return res.json()
  },

  brief: async (projectId: string, revision: number): Promise<{ content: string } | null> => {
    const res = await apiFetch(`/api/genesis/${projectId}/revisions/${revision}/brief`)
    if (!res.ok) return null
    return res.json()
  },

  revisionMeta: async (projectId: string, revision: number): Promise<Record<string, any> | null> => {
    const res = await apiFetch(`/api/genesis/${projectId}/revisions/${revision}`)
    if (!res.ok) return null
    return res.json()
  },

  cancel: async (projectId: string): Promise<void> => {
    await apiFetch(`/api/genesis/${projectId}/cancel`, { method: 'DELETE' })
  },

  draft: async (projectId: string): Promise<{
    has_draft: boolean
    has_brief: boolean
    metadata: {
      project_id: string
      tenant_id: string
      is_draft: boolean
      triggered_by?: string
      user_id?: string
      created_at: string
    } | null
    documents: string[]
  }> => {
    const res = await apiFetch(`/api/genesis/${projectId}/draft`)
    if (!res.ok) return { has_draft: false, has_brief: false, metadata: null, documents: [] }
    return res.json()
  },

  saveDraft: async (projectId: string, message?: string): Promise<any> => {
    const res = await apiFetch(`/api/genesis/${projectId}/draft/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message: message ?? '' }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to save revision' }))
      throw new Error(err.detail || 'Failed to save revision')
    }
    return res.json()
  },

  discardDraft: async (projectId: string): Promise<void> => {
    await apiFetch(`/api/genesis/${projectId}/draft`, { method: 'DELETE' })
  },

  draftDocument: async (projectId: string, docType: string): Promise<{ content: string } | null> => {
    const res = await apiFetch(`/api/genesis/${projectId}/draft/${docType}`)
    if (!res.ok) return null
    return res.json()
  },

  draftBrief: async (projectId: string): Promise<{ content: string } | null> => {
    const res = await apiFetch(`/api/genesis/${projectId}/draft/brief`)
    if (!res.ok) return null
    return res.json()
  },

  chat: async (
    projectId: string,
    message: string,
    opts: ChatOptions = {}
  ): Promise<ReadableStream<Uint8Array> | null> => {
    return chat.sendMessage('genesis', message, { ...opts, projectId })
  },
}

// ─── Usage ────────────────────────────────────────────────────────────────────

export const usage = {
  summary: async (filters: UsageSummaryFilters = {}) => {
    const params = new URLSearchParams()
    if (filters.project_id) params.set('project_id', filters.project_id)
    if (filters.agent_id) params.set('agent_id', filters.agent_id)
    if (filters.start_date) params.set('start_date', filters.start_date)
    if (filters.end_date) params.set('end_date', filters.end_date)

    const res = await apiFetch(`/api/usage?${params}`)
    if (!res.ok) throw new Error('Failed to fetch usage summary')
    return res.json()
  },

  requests: async (filters: UsageSummaryFilters & { limit?: number; offset?: number } = {}) => {
    const params = new URLSearchParams()
    if (filters.project_id) params.set('project_id', filters.project_id)
    if (filters.agent_id) params.set('agent_id', filters.agent_id)
    if (filters.user_id) params.set('user_id', filters.user_id)
    if (filters.start_date) params.set('start_date', filters.start_date)
    if (filters.end_date) params.set('end_date', filters.end_date)
    if (filters.limit) params.set('limit', String(filters.limit))
    if (filters.offset) params.set('offset', String(filters.offset))

    const res = await apiFetch(`/api/usage/requests?${params}`)
    if (!res.ok) throw new Error('Failed to fetch usage requests')
    return res.json()
  },

  // SCRUM-92: used to take only {project_id, days}, so the chart could never be scoped to
  // the same start_date/end_date/agent_id the summary cards were filtered by — the two
  // panels disagreed on everything except project. Same filter shape as summary()/requests()
  // now (minus user_id, which the backend's daily/summary/by-agent queries don't support —
  // only the per-request table does).
  daily: async (filters: UsageSummaryFilters & { days?: number } = {}) => {
    const params = new URLSearchParams()
    if (filters.project_id) params.set('project_id', filters.project_id)
    if (filters.agent_id) params.set('agent_id', filters.agent_id)
    if (filters.start_date) params.set('start_date', filters.start_date)
    if (filters.end_date) params.set('end_date', filters.end_date)
    if (filters.days) params.set('days', String(filters.days))
    const res = await apiFetch(`/api/usage/daily?${params}`)
    if (!res.ok) throw new Error('Failed to fetch daily usage')
    return res.json()
  },

  /** Signed-in user's 5-hour + weekly allowance status. Read-only — never rolls a window. */
  limits: async (): Promise<UsageLimitStatus> => {
    const res = await apiFetch('/api/usage/limits')
    if (!res.ok) throw new Error('Failed to fetch usage limits')
    return res.json()
  },

  /**
   * SCRUM-165 — Administrator-only. A named user's allowance status plus how many
   * times they've been reset in the last 30 days. 403s for anyone but the
   * Administrator account; the caller should treat a 403 as "hide this section"
   * rather than an error to surface, since a non-admin is expected to see one.
   */
  adminLimitStatus: async (email: string): Promise<UsageLimitStatus & {
    email: string; reset_count_30d: number; unmetered: boolean
  }> => {
    const res = await apiFetch(`/api/usage/limits/admin?${new URLSearchParams({ email })}`)
    if (!res.ok) throw new Error(res.status === 403 ? 'admin-only' : 'Failed to fetch admin usage status')
    return res.json()
  },

  /**
   * SCRUM-165 — reset one user's window(s). `reason` is mandatory (min 10 chars,
   * enforced again at the API and the database) and every reset is written to a
   * durable, queryable audit table — see kael.usage_limit_resets.
   */
  adminResetLimit: async (
    email: string, windowKinds: UsageWindowKind[], reason: string
  ): Promise<{ email: string; reset_window_kinds: UsageWindowKind[]; limits: UsageLimitStatus }> => {
    const res = await apiFetch('/api/usage/limits/admin/reset', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, window_kinds: windowKinds, reason }),
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      // FastAPI's own 422 (pydantic validation) returns `detail` as an ARRAY of objects,
      // not a string — rendering that straight into an Error produced "[object Object]"
      // in the toast. The route's hand-written 422/503 details are strings; take those.
      const detail = typeof body?.detail === 'string' ? body.detail : null
      throw new Error(detail || 'Failed to reset usage limit')
    }
    return res.json()
  },

  /**
   * SCRUM-165 — reset EVERY user's window(s) in the tenant. `confirm` must be the literal
   * "RESET ALL"; the single-user call has no equivalent because its blast radius is one
   * person. Returns per-user outcomes: a partial run reports which users it missed rather
   * than reporting success for all of them.
   */
  adminResetAllLimits: async (
    windowKinds: UsageWindowKind[], reason: string, confirm: string
  ): Promise<{
    reset_window_kinds: UsageWindowKind[]; candidates: number; reset_count: number
    reset: string[]
    skipped: { user_key: string; why: string }[]
    failed: { user_key: string; error: string }[]
  }> => {
    const res = await apiFetch('/api/usage/limits/admin/reset-all', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ window_kinds: windowKinds, reason, confirm }),
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      const detail = typeof body?.detail === 'string' ? body.detail : null
      throw new Error(detail || 'Failed to reset all usage limits')
    }
    return res.json()
  },

  /**
   * Global AI usage metering kill switch. Administrator-only, same 403-as-"hide this
   * section" contract as {@link adminLimitStatus}. Applies to every user, not one.
   */
  adminMeteringState: async (): Promise<UsageMeteringState> => {
    const res = await apiFetch('/api/usage/limits/admin/metering')
    if (!res.ok) throw new Error(res.status === 403 ? 'admin-only' : 'Failed to fetch metering state')
    return res.json()
  },

  /** Flip the global metering switch. `reason` is mandatory (min 10 chars), enforced again server-side. */
  adminSetMetering: async (enabled: boolean, reason: string): Promise<UsageMeteringState> => {
    const res = await apiFetch('/api/usage/limits/admin/metering', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled, reason }),
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      const detail = typeof body?.detail === 'string' ? body.detail : null
      throw new Error(detail || 'Failed to update metering state')
    }
    return res.json()
  },
}

// ─── Memory ───────────────────────────────────────────────────────────────────

export type MemoryScope = 'user' | 'project' | 'tenant'

export const memory = {
  /** List memories. Defaults to user scope (cross-project). */
  list: async (scope: MemoryScope = 'user', projectId?: string) => {
    const p = new URLSearchParams({ scope })
    if (projectId) p.set('project_id', projectId)
    const res = await apiFetch(`/api/memory?${p}`)
    if (!res.ok) return []
    return (await res.json()).memories || []
  },

  /** Add a memory. Defaults to user scope. */
  create: async (content: string, scope: MemoryScope = 'user', projectId?: string | null) => {
    const res = await apiFetch('/api/memory', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content, scope, project_id: projectId ?? null }),
    })
    if (!res.ok) throw new Error('Failed to add memory')
    return res.json()
  },

  delete: async (memoryId: string, scope: MemoryScope = 'user', projectId?: string) => {
    const p = new URLSearchParams({ scope })
    if (projectId) p.set('project_id', projectId)
    const res = await apiFetch(`/api/memory/${memoryId}?${p}`, { method: 'DELETE' })
    return res.ok
  },
}

// ─── Knowledge ────────────────────────────────────────────────────────────────

export const knowledge = {
  search: async (query: string, projectId?: string) => {
    const params = new URLSearchParams({ query })
    if (projectId) params.set('project_id', projectId)

    const res = await apiFetch(`/api/knowledge/search?${params}`)
    if (!res.ok) throw new Error('Knowledge search failed')
    return res.json()
  },

  reindex: async (projectId?: string) => {
    const res = await apiFetch('/api/knowledge/reindex', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ project_id: projectId }),
    })
    return res.ok
  },

  sync: async (params?: { project_id?: string; since_hours?: number }): Promise<{ synced: number }> => {
    const p = new URLSearchParams()
    if (params?.project_id) p.set('project_id', params.project_id)
    if (params?.since_hours != null) p.set('since_hours', String(params.since_hours))
    const res = await apiFetch(`/api/knowledge/sync?${p}`, { method: 'POST' })
    if (!res.ok) throw new Error('Knowledge sync failed')
    return res.json()
  },
}

// ─── Meetings ────────────────────────────────────────────────────────────────

export const meetings = {
  status: async () => {
    const res = await apiFetch('/api/meetings/status')
    if (!res.ok) throw new Error('Failed to fetch meeting status')
    return res.json()
  },

  join: async (meetingUrl: string, opts: { title?: string; project?: string } = {}) => {
    const res = await apiFetch('/api/meetings/join', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ meeting_url: meetingUrl, ...opts }),
    })
    if (!res.ok) throw new Error('Failed to trigger meeting join')
    return res.json()
  },

  reset: async () => {
    const res = await apiFetch('/api/meetings/reset', { method: 'POST' })
    return res.ok
  },

  list: async () => {
    const res = await apiFetch('/api/meetings/list')
    if (!res.ok) throw new Error('Failed to list meetings')
    return res.json()
  },

  outputs: async (meetingId: string) => {
    const res = await apiFetch(`/api/meetings/${meetingId}/outputs`)
    if (!res.ok) throw new Error('Failed to fetch meeting outputs')
    return res.json()
  },

  transcript: async (meetingId: string) => {
    const res = await apiFetch(`/api/meetings/${meetingId}/transcript`)
    if (!res.ok) throw new Error('No transcript found')
    return res.json()
  },

  notes: async (meetingId: string) => {
    const res = await apiFetch(`/api/meetings/${meetingId}/notes`)
    if (!res.ok) throw new Error('No notes found')
    return res.json()
  },

  notesJson: async (meetingId: string) => {
    const res = await apiFetch(`/api/meetings/${meetingId}/notes-json`)
    if (!res.ok) throw new Error('No structured notes found')
    return res.json()
  },

  brief: async (meetingId: string) => {
    const res = await apiFetch(`/api/meetings/${meetingId}/brief`)
    if (!res.ok) throw new Error('No brief found')
    return res.json()
  },

  generateNotes: async (meetingId: string) => {
    const res = await apiFetch(`/api/meetings/${meetingId}/generate-notes`, { method: 'POST' })
    if (!res.ok) throw new Error('Failed to trigger notes generation')
    return res.json()
  },

  generateBrief: async (meetingId: string) => {
    const res = await apiFetch(`/api/meetings/${meetingId}/generate-brief`, { method: 'POST' })
    if (!res.ok) throw new Error('Failed to trigger brief generation')
    return res.json()
  },

  pushBrief: async (meetingId: string, projectId: string) => {
    const res = await apiFetch(`/api/meetings/${meetingId}/push-brief`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ project_id: projectId }),
    })
    if (!res.ok) throw new Error('Failed to push brief to platform')
    return res.json()
  },

  end: async (meetingId: string) => {
    const res = await apiFetch(`/api/meetings/${meetingId}/end`, { method: 'POST' })
    if (!res.ok) throw new Error('Failed to end meeting')
    return res.json()
  },

  delete: async (meetingId: string) => {
    const res = await apiFetch(`/api/meetings/${meetingId}`, { method: 'DELETE' })
    if (!res.ok) throw new Error('Failed to delete meeting')
    return res.json()
  },

  resolveObsr: async (obsrId: string) => {
    const res = await apiFetch(`/api/meetings/resolve-obsr/${encodeURIComponent(obsrId)}`)
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error(body.error || 'OBSR Meeting not found')
    }
    return res.json() as Promise<{ meeting_link: string; title: string; project: string }>
  },

  pushToFrappe: async (meetingId: string) => {
    const res = await apiFetch(`/api/meetings/${meetingId}/push-to-frappe`, { method: 'POST' })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error(body.error || 'Failed to push to Frappe')
    }
    return res.json()
  },
}

// ─── Recordings ───────────────────────────────────────────────────────────────

export interface FrappeRecording {
  name: string
  project?: string
  title?: string
  status?: 'Draft' | 'Processing' | 'Completed' | 'Failed' | string
  audio_file?: string | null
  duration_seconds?: number | null
  transcript?: string | null
  creation?: string
  created_at?: string
  modified?: string
}

export interface FrappeSDRecording {
  name: string
  project?: string
  subject?: string
  recording_status?: 'Draft' | 'Processing' | 'Completed' | 'Failed' | string
  document?: string | null
  description?: string | null
  creation?: string
  modified?: string
}

export const recordings = {
  list: async (projectId?: string): Promise<FrappeRecording[]> => {
    const qs = projectId ? `?project=${encodeURIComponent(projectId)}` : ''
    const res = await apiFetch(`/api/recordings${qs}`)
    if (!res.ok) return []
    const data = await res.json()
    const result = data.recordings ?? data.data ?? data
    return Array.isArray(result) ? result : []
  },

  get: async (id: string): Promise<FrappeRecording> => {
    const res = await apiFetch(`/api/recordings/${encodeURIComponent(id)}`)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const json = await res.json()
    return json.data ?? json
  },

  update: async (id: string, patch: Partial<FrappeRecording>): Promise<FrappeRecording> => {
    const res = await apiFetch(`/api/recordings/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return res.json()
  },
}

export const siteDiaryRecordings = {
  list: async (projectId?: string): Promise<FrappeSDRecording[]> => {
    const qs = projectId ? `?project=${encodeURIComponent(projectId)}` : ''
    const res = await apiFetch(`/api/site-diary-recordings${qs}`)
    if (!res.ok) return []
    const data = await res.json()
    const result = data.data ?? data
    return Array.isArray(result) ? result : []
  },

  get: async (id: string): Promise<FrappeSDRecording> => {
    const res = await apiFetch(`/api/site-diary-recordings/${encodeURIComponent(id)}`)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const json = await res.json()
    return json.data ?? json
  },

  update: async (id: string, patch: Partial<FrappeSDRecording>): Promise<FrappeSDRecording> => {
    const res = await apiFetch(`/api/site-diary-recordings/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return res.json()
  },
}

// ─── Files (per-session artifacts + MD→PDF) ────────────────────────────────

export interface SessionArtifact {
  artifact_id: string
  filename: string
  size: number
  url: string
  title: string
  description?: string
  pdf_url?: string
  pdf_filename?: string
  modified_at?: string
}

export const files = {
  /** List every artifact written into the session's artifacts/ folder. */
  listArtifacts: async (sessionId: string, projectId: string = ''): Promise<SessionArtifact[]> => {
    const qs = projectId ? `?project_id=${encodeURIComponent(projectId)}` : ''
    const res = await apiFetch(`/api/sessions/${sessionId}/artifacts${qs}`)
    if (!res.ok) return []
    const data = await res.json()
    return data.artifacts || []
  },

  /** Render a stored .md artifact as a Kael-branded PDF. Returns Blob for download. */
  mdToPdf: async (sessionId: string, filename: string, opts: { projectId?: string; title?: string } = {}): Promise<Blob> => {
    const res = await apiFetch('/api/files/md-to-pdf', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: sessionId,
        project_id: opts.projectId || '',
        filename,
        title: opts.title,
      }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }))
      throw new Error(err.detail || `HTTP ${res.status}`)
    }
    return res.blob()
  },
}

// ─── Schedule ────────────────────────────────────────────────────────────────

export interface ScheduleRevision {
  revision: number
  revision_label: string
  triggered_by?: string
  user_id?: string
  created_at?: string
  saved_at?: string
  status?: string
  /** Free-text "commit message" entered by the user when the revision was saved. */
  message?: string
}

export interface ScheduleDraft {
  has_draft: boolean
  metadata: {
    is_draft: true
    triggered_by?: string
    user_id?: string
    created_at?: string
    status?: string
    mode?: string
    completed_at?: string
  } | null
}

export const schedule = {
  generate: async (projectId: string, mode: 'boq' | 'genesis' = 'boq') => {
    const res = await apiFetch(`/api/schedule/${projectId}/generate?mode=${mode}`, { method: 'POST' })
    if (!res.ok) throw new Error('Failed to start schedule generation')
    return res.json()
  },

  status: async (projectId: string) => {
    const res = await apiFetch(`/api/schedule/${projectId}/status`)
    if (!res.ok) throw new Error('Failed to get schedule status')
    return res.json()
  },

  /**
   * Load the gantt JSON for the project.
   * - revision='' (default): server resolves draft → latest revision → legacy root
   * - revision='draft': force draft
   * - revision='R{n}': force a specific revision
   */
  data: async (projectId: string, revision: string = '') => {
    const qs = revision ? `?revision=${encodeURIComponent(revision)}` : ''
    const res = await apiFetch(`/api/schedule/${projectId}/data${qs}`)
    if (!res.ok) throw new Error('Schedule data not found')
    return res.json()
  },

  cancel: async (projectId: string) => {
    const res = await apiFetch(`/api/schedule/${projectId}/cancel`, { method: 'DELETE' })
    if (!res.ok) throw new Error('Failed to cancel generation')
    return res.json()
  },


  draft: async (projectId: string): Promise<ScheduleDraft> => {
    const res = await apiFetch(`/api/schedule/${projectId}/draft`)
    if (!res.ok) return { has_draft: false, metadata: null }
    return res.json()
  },

  saveDraft: async (projectId: string, message?: string): Promise<ScheduleRevision> => {
    const res = await apiFetch(`/api/schedule/${projectId}/draft/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message: message ?? '' }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Failed to save revision' }))
      throw new Error(err.detail || 'Failed to save revision')
    }
    return res.json()
  },

  discardDraft: async (projectId: string): Promise<void> => {
    await apiFetch(`/api/schedule/${projectId}/draft`, { method: 'DELETE' })
  },

  revisions: async (projectId: string): Promise<{ project_id: string; revisions: ScheduleRevision[] }> => {
    const res = await apiFetch(`/api/schedule/${projectId}/revisions`)
    if (!res.ok) return { project_id: projectId, revisions: [] }
    return res.json()
  },

  /** Resolve the URL for a PDF download (caller passes to apiFetch / download helper). */
  pdfPath: (projectId: string, revision: string = 'draft'): string =>
    revision === 'draft'
      ? `/api/schedule/${projectId}/draft/pdf`
      : `/api/schedule/${projectId}/revisions/${revision}/pdf`,

  saveToFrappe: async (projectId: string): Promise<{
    status: string
    result?: { created: number; updated: number; skipped: number; deleted: number; errors?: string[] }
    revision?: { revision: number; revision_label: string } | null
    error?: string
  }> => {
    const res = await apiFetch(`/api/schedule/${projectId}/save-to-frappe`, { method: 'POST' })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error((err as any).detail || 'Failed to save schedule')
    }
    return res.json()
  },
}

// ─── Config ───────────────────────────────────────────────────────────────────

/** FastAPI answers errors as `{"detail": "..."}`. Read it without an `any` cast. */
function errorDetail(body: unknown, fallback: string): string {
  if (body && typeof body === 'object' && 'detail' in body) {
    const detail = (body as { detail?: unknown }).detail
    if (typeof detail === 'string' && detail) return detail
  }
  return fallback
}

export interface ModelOption {
  id: string
  label: string
  provider: string
  provider_label: string
  kind: string
  /** Served by a self-hosted / OpenAI-compatible endpoint — no API key involved. */
  local: boolean
  context_window: number
}

export interface ModelCatalog {
  models: ModelOption[]
  tiers: { default: string; large: string; lite: string }
  chat_modes: Record<string, string>
  agents: string[]
  /** What each agent resolves to right now, overrides included. */
  agent_models: Record<string, string>
  /** Only the agents pinned by an env override — these are the ones with a Reset action. */
  agent_overrides_env: Record<string, string>
  /** The coded default per agent, for showing what a Reset would restore. */
  agent_defaults: Record<string, string>
}

export interface LlmProvider {
  name: string
  kind: string
  label: string
  base_url: string
  /** NAME of the environment variable holding this provider's key — never the key. */
  key_env: string
  /** Fingerprint of the resolved value, so the screen can show the variable points somewhere. */
  api_key_masked: string
  /** Whether `key_env` actually resolves in the server's environment. */
  has_api_key: boolean
  requires_api_key: boolean
  models: string[]
  surface: string
  builtin: boolean
  legacy: boolean
}

export interface ProviderUpsert {
  kind: string
  label?: string
  base_url?: string
  /**
   * NAME of the env var holding the key (e.g. "DASHSCOPE_API_KEY"). There is no field for the
   * key itself — its value is added to the .env file by hand, so no secret is ever sent here.
   */
  key_env?: string
  models: string[]
  surface?: string
}

export interface ProviderTest {
  kind?: string
  base_url?: string
  /** A variable name; the server resolves its value from its own environment. */
  key_env?: string
  provider?: string
}

export interface ProviderTestResult {
  ok: boolean
  status?: number
  detail?: string
  url?: string
  models?: string[]
}

export const config = {
  clients: async () => {
    const res = await apiFetch('/api/config/clients')
    if (!res.ok) return []
    const data = await res.json()
    return data.clients || []
  },

  models: async () => {
    const res = await apiFetch('/api/config/models')
    if (!res.ok) return {}
    return res.json()
  },

  agents: async () => {
    const res = await apiFetch('/api/config/agents')
    if (!res.ok) return []
    const data = await res.json()
    return data.agents || []
  },

  /**
   * Every model a user may pick, plus which model each agent currently resolves to.
   * Carries model ids and provider labels only — never an API key.
   */
  catalog: async (): Promise<ModelCatalog> => {
    const res = await apiFetch('/api/config/models/catalog')
    if (!res.ok) throw new Error('Failed to load model catalog')
    return res.json()
  },

  providers: async (): Promise<{ providers: LlmProvider[]; kinds: string[]; env_file: string }> => {
    const res = await apiFetch('/api/config/providers')
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(errorDetail(err, 'Failed to load providers'))
    }
    return res.json()
  },

  saveProvider: async (name: string, body: ProviderUpsert) => {
    const res = await apiFetch(`/api/config/providers/${encodeURIComponent(name)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(errorDetail(err, 'Failed to save provider'))
    }
    return res.json()
  },

  deleteProvider: async (name: string) => {
    const res = await apiFetch(`/api/config/providers/${encodeURIComponent(name)}`, { method: 'DELETE' })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(errorDetail(err, 'Failed to delete provider'))
    }
    return res.json()
  },

  /** Probe an endpoint before saving it, so a typo fails here instead of mid-chat. */
  testProvider: async (body: ProviderTest): Promise<ProviderTestResult> => {
    const res = await apiFetch('/api/config/providers/test', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(errorDetail(err, 'Connection test failed'))
    }
    return res.json()
  },

  setAgentModel: async (agent: string, modelId: string) => {
    const res = await apiFetch(`/api/config/models/agent/${encodeURIComponent(agent)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ model_id: modelId }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(errorDetail(err, 'Failed to set agent model'))
    }
    return res.json()
  },

  clearAgentModel: async (agent: string) => {
    const res = await apiFetch(`/api/config/models/agent/${encodeURIComponent(agent)}`, { method: 'DELETE' })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(errorDetail(err, 'Failed to clear agent model'))
    }
    return res.json()
  },

  setTiers: async (tiers: { default?: string; large?: string; lite?: string }) => {
    const res = await apiFetch('/api/config/models/tiers', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(tiers),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(errorDetail(err, 'Failed to save tiers'))
    }
    return res.json()
  },

  /**
   * Resolved tenant client's allow-listed agent ids.
   * Returns `null` on any failure so callers can fail open (see
   * {@link isAgentVisible}). Empty array = tenant has no allow-list (all allowed).
   */
  allowedAgents: async (): Promise<string[] | null> => {
    try {
      const res = await apiFetch('/api/config/allowed-agents')
      if (!res.ok) return null
      const data = await res.json()
      return data.allowed_agents ?? []
    } catch {
      return null
    }
  },
}

// ─── Legacy compat (used by existing components) ──────────────────────────────

export const api = {
  checkHealth: health.check,

  getAgents: async () => {
    // Gate experimental agents on the tenant's allow-list (fail open on error).
    const allowed = await config.allowedAgents()
    return KAEL_AGENTS
      .filter((a) => a.type === 'agent' && isAgentVisible(a, allowed))
      .map((a) => ({
        id: a.id,
        name: a.name,
        description: a.description,
      }))
  },

  getTeams: async () => KAEL_AGENTS.filter((a) => a.type === 'team').map((a) => ({
    id: a.id,
    name: a.name,
    description: a.description,
  })),

  getWorkflows: async () => [],

  sendAgentMessage: async (
    agentId: string,
    message: string,
    sessionId?: string,
    _dbId?: string,
    files?: File[],
    signal?: AbortSignal,
    uploadIds?: string[],
    mode?: string,
    model?: string,
  ) => {
    const { projectId } = getSettings()
    return chat.sendMessage(agentId, message, { sessionId, projectId, files, signal, uploadIds, mode, model })
  },

  sendTeamMessage: async (
    teamId: string,
    message: string,
    sessionId?: string,
    _dbId?: string,
    files?: File[],
    signal?: AbortSignal,
    uploadIds?: string[],
    mode?: string,
    model?: string,
  ) => {
    const { projectId } = getSettings()
    return chat.sendTeamMessage(teamId, message, { sessionId, projectId, files, signal, uploadIds, mode, model })
  },

  runWorkflow: async (
    _workflowId: string,
    _message: string,
    _sessionId?: string,
    _dbId?: string,
    _signal?: AbortSignal
  ) => {
    throw new Error('Workflows not supported in Kael Agents')
  },

  getSessions: async (
    _type: 'agent' | 'team' | 'workflow',
    agentId: string,
    _dbId?: string
  ) => {
    const { projectId } = getSettings()
    const list = await sessions.list({
      agent_id: agentId,
      project_id: projectId || undefined,
    })
    return { data: list }
  },

  createSession: async (
    _type: 'agent' | 'team' | 'workflow',
    _config: Record<string, any> = {},
    _dbId?: string
  ) => {
    // Sessions are auto-created on first chat
    return { session_id: undefined }
  },

  deleteSession: async (sessionId: string, _dbId?: string) => {
    await sessions.delete(sessionId)
    return new Response(null, { status: 200 })
  },

  /**
   * Abort a session server-side: stops the active run AND any spawned sub-agent
   * tasks. Backend contract: POST /api/sessions/{id}/abort →
   * { aborted, tasks_stopped, session_id }. This is the real cancellation path
   * (cancellation is session-scoped, not run-scoped).
   */
  abortSession: async (
    sessionId: string,
  ): Promise<{ aborted: boolean; tasks_stopped: number; session_id: string }> => {
    const res = await apiFetch(`/api/sessions/${encodeURIComponent(sessionId)}/abort`, {
      method: 'POST',
    })
    if (!res.ok) throw new Error(`Abort session failed: ${res.status}`)
    return res.json()
  },

  // Thin back-compat wrappers around the session-scoped abort. The run-level cancel
  // endpoints never existed server-side. Callers that pass a sessionId get a real
  // abort; older 2-arg call sites still compile (and no-op as before).
  cancelAgentRun: async (_agentId: string, _runId: string, sessionId?: string) => {
    if (sessionId) return api.abortSession(sessionId)
    return { cancelled: true }
  },

  cancelTeamRun: async (_teamId: string, _runId: string, sessionId?: string) => {
    if (sessionId) return api.abortSession(sessionId)
    return { cancelled: true }
  },

  cancelWorkflowRun: async (_workflowId: string, _runId: string) => {
    return { cancelled: true }
  },

  continueAgentRun: async (
    agentId: string,
    runId: string,
    sessionId: string,
    tools: Array<{ tool_call_id: string; result: unknown }>,
  ): Promise<ReadableStream<Uint8Array> | null> => {
    const params = new URLSearchParams({ session_id: sessionId })
    const res = await apiFetch(`/api/chat/${agentId}/runs/${runId}/continue?${params}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(tools),
    })
    if (!res.ok) throw new Error(`Continue agent run failed: ${res.status}`)
    return res.body
  },

  /**
   * Restart an interrupted run. Success is an SSE stream shaped like /confirm.
   *
   * A 409 means the server cannot continue this run (today: any run killed by a restart);
   * its `detail` is safe to show the user verbatim.
   */
  resumeRun: async (
    agentId: string,
    runId: string,
    sessionId: string,
  ): Promise<ReadableStream<Uint8Array> | null> => {
    const params = new URLSearchParams({ session_id: sessionId })
    const path = `/api/chat/${encodeURIComponent(agentId)}/runs/${encodeURIComponent(runId)}/resume?${params}`
    const res = await apiFetch(path, { method: 'POST' })
    if (!res.ok) {
      const body = await res.json().catch(() => null)
      throw new Error(errorDetail(body, `Resume failed (${res.status})`))
    }
    return res.body
  },

  continueTeamRun: async (
    teamId: string,
    runId: string,
    sessionId: string,
    requirements: Array<Record<string, unknown>>,
  ): Promise<ReadableStream<Uint8Array> | null> => {
    const params = new URLSearchParams({ session_id: sessionId })
    const res = await apiFetch(`/api/chat/team/${teamId}/runs/${runId}/continue?${params}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requirements),
    })
    if (!res.ok) throw new Error(`Continue team run failed: ${res.status}`)
    return res.body
  },
}

// ─── Workflows (Wave 3 dynamic runtime) ────────────────────────────────────────
// Contract frozen in docs/workflow-events.md. These hit the NEW plural
// /api/workflows routes — distinct from the OLD singular /api/workflow (G04 DAG).

/** Summary row returned by GET /api/workflows (WorkflowStore.list_runs). */
export interface WorkflowRunSummary {
  run_id: string
  name: string
  status: 'pending' | 'running' | 'completed' | 'failed' | string
  reason?: string | null
  created_at?: string
  finished_at?: string | null
  usage?: { input_tokens?: number; output_tokens?: number; agents?: number; cost_usd?: number }
  /** Present on some backends; the list may not carry it. */
  session_id?: string
  root_session_id?: string
}

/** A saved workflow command (workflows/commands/{name}.workflow.js + meta). */
export interface WorkflowCommand {
  name: string
  description?: string
  version?: string | null
  meta?: Record<string, unknown>
  created_at?: string
  updated_at?: string
}

export const workflows = {
  /** List tenant workflow runs (newest first). Optionally filter by session or status. */
  list: async (params: { session_id?: string; status?: string } = {}): Promise<WorkflowRunSummary[]> => {
    const p = new URLSearchParams()
    if (params.session_id) p.set('session_id', params.session_id)
    if (params.status) p.set('status', params.status)
    const qs = p.toString()
    const res = await apiFetch(`/api/workflows${qs ? `?${qs}` : ''}`)
    if (!res.ok) {
      if (res.status === 401 || res.status === 404) return []
      throw new Error(`Failed to fetch workflows (${res.status})`)
    }
    const data = await res.json().catch(() => ({}))
    return data.runs ?? data.workflows ?? data.data ?? (Array.isArray(data) ? data : [])
  },

  /** Full run.json document for one run. */
  get: async (runId: string): Promise<any | null> => {
    const res = await apiFetch(`/api/workflows/${encodeURIComponent(runId)}`)
    if (!res.ok) {
      if (res.status === 404) return null
      throw new Error(`Failed to fetch workflow run (${res.status})`)
    }
    const data = await res.json()
    // Some backends wrap the doc under { run: ... } / { workflow: ... }.
    return data.run ?? data.workflow ?? data
  },

  /** Request a graceful stop of a running workflow. */
  stop: async (runId: string): Promise<boolean> => {
    const res = await apiFetch(`/api/workflows/${encodeURIComponent(runId)}/stop`, { method: 'POST' })
    return res.ok
  },

  /** Re-run a stored workflow (cached agent.run results replay instantly). */
  resume: async (runId: string): Promise<{ run_id?: string } | boolean> => {
    const res = await apiFetch(`/api/workflows/${encodeURIComponent(runId)}/resume`, { method: 'POST' })
    if (!res.ok) throw new Error(`Failed to resume workflow (${res.status})`)
    const ct = res.headers.get('content-type') || ''
    if (ct.includes('application/json')) return res.json().catch(() => true)
    return true
  },

  /** List saved workflow commands. */
  commands: async (): Promise<WorkflowCommand[]> => {
    const res = await apiFetch('/api/workflows/commands')
    if (!res.ok) {
      if (res.status === 401 || res.status === 404) return []
      throw new Error(`Failed to fetch workflow commands (${res.status})`)
    }
    const data = await res.json().catch(() => ({}))
    return data.commands ?? data.data ?? (Array.isArray(data) ? data : [])
  },

  /** Save a workflow command (POST /api/workflows/commands). */
  saveCommand: async (body: {
    name: string
    script: string
    meta?: Record<string, unknown>
  }): Promise<WorkflowCommand> => {
    const res = await apiFetch('/api/workflows/commands', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!res.ok) throw new Error(`Failed to save workflow command (${res.status})`)
    return res.json()
  },

  /**
   * Full URL for the per-run SSE event stream, for use with EventSource.
   * EventSource can't set an Authorization header, so we fall back to cookie auth
   * (withCredentials) and append the bearer token as a query param when present —
   * FastAPI ignores query params it doesn't declare, so this is safe either way.
   */
  eventsUrl: (runId: string): string => {
    const base = `${API_BASE}/api/workflows/${encodeURIComponent(runId)}/events`
    const token = getApiToken()
    return token ? `${base}?token=${encodeURIComponent(token)}` : base
  },
}

// ─── Session agent-event stream (sub-agent task lifecycle) ──────────────────────
// SSE GET /api/events/{session_id}?event_types=… emits `event: {type}\ndata: {json}`.
// Task events: task_spawned|task_running|task_completed|task_failed|task_cancelled|
// task_orphaned and task_tool_call. The stream never auto-closes (300s keepalive) —
// callers must close it on unmount / session change.

/** The task event types the session stream should deliver. */
export const SESSION_TASK_EVENT_TYPES = [
  'task_spawned',
  'task_running',
  'task_completed',
  'task_failed',
  'task_cancelled',
  'task_orphaned',
  'task_tool_call',
  'subagent_load_warning',
] as const

export const events = {
  /**
   * Full URL for the per-session SSE task-event stream, for use with EventSource.
   * Mirrors workflows.eventsUrl: EventSource can't set an Authorization header, so
   * we rely on cookie auth (withCredentials) and append the bearer token as a query
   * param when present (FastAPI ignores query params it doesn't declare).
   */
  sessionEventsUrl: (sessionId: string): string => {
    const p = new URLSearchParams({ event_types: SESSION_TASK_EVENT_TYPES.join(',') })
    const token = getApiToken()
    if (token) p.set('token', token)
    return `${API_BASE}/api/events/${encodeURIComponent(sessionId)}?${p.toString()}`
  },
}

// ─── Tasks (spawned sub-agent hydration) ────────────────────────────────────────

export interface AgentTaskRecord {
  task_id: string
  agent_id: string
  status: string
  summary?: string
  background?: boolean
  usage?: { input_tokens?: number; output_tokens?: number }
  [key: string]: any
}

export const tasks = {
  /** Snapshot of spawned sub-agent tasks for a session (hydration on load/reconnect). */
  list: async (sessionId: string): Promise<AgentTaskRecord[]> => {
    const p = new URLSearchParams({ session_id: sessionId })
    const res = await apiFetch(`/api/tasks?${p}`)
    if (!res.ok) {
      if (res.status === 401 || res.status === 404) return []
      throw new Error(`Failed to fetch tasks (${res.status})`)
    }
    const data = await res.json().catch(() => ({}))
    return data.tasks ?? data.data ?? (Array.isArray(data) ? data : [])
  },
}

// ─── Agents (tool introspection) ────────────────────────────────────────────────

export interface AgentToolkitInfo {
  toolkit: string
  tools: { name: string; description?: string }[]
}

export interface AgentToolsResponse {
  agent: string
  description?: string
  model_id?: string
  toolkits: AgentToolkitInfo[]
}

export const agents = {
  /** Toolkits + tools an agent exposes: GET /api/agents/{agentId}/tools. */
  tools: async (agentId: string): Promise<AgentToolsResponse> => {
    const res = await apiFetch(`/api/agents/${encodeURIComponent(agentId)}/tools`)
    if (!res.ok) throw new Error(`Failed to fetch agent tools (${res.status})`)
    return res.json()
  },
}

// ─── Prompt Rules ─────────────────────────────────────────────────────────────

export interface PromptRule {
  id: number
  client_id: string
  project_id: string | null
  title: string
  rule_text: string
  rule_type: string
  priority: number
  created_at: string
  updated_at: string
}

export interface PromptRuleCreate {
  title: string
  rule_text: string
  project_id?: string | null
  rule_type?: string
  priority?: number
}

export interface PromptRuleUpdate {
  title?: string
  rule_text?: string
  priority?: number
}

export const promptRules = {
  list: async (projectId?: string): Promise<PromptRule[]> => {
    const p = new URLSearchParams()
    if (projectId) p.set('project_id', projectId)
    const res = await apiFetch(`/api/prompt-rules?${p}`)
    if (!res.ok) throw new Error('Failed to fetch prompt rules')
    return (await res.json()).rules ?? []
  },

  create: async (data: PromptRuleCreate): Promise<PromptRule> => {
    const res = await apiFetch('/api/prompt-rules', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new Error('Failed to create rule')
    return (await res.json()).rule
  },

  update: async (id: number, data: PromptRuleUpdate): Promise<PromptRule> => {
    const res = await apiFetch(`/api/prompt-rules/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new Error('Failed to update rule')
    return (await res.json()).rule
  },

  delete: async (id: number): Promise<void> => {
    const res = await apiFetch(`/api/prompt-rules/${id}`, { method: 'DELETE' })
    if (!res.ok) throw new Error('Failed to delete rule')
  },
}

// ─── Cron Jobs ────────────────────────────────────────────────────────────────

export interface CronJob {
  job_id: string
  job_type: string
  tenant_id: string
  cron_expression: string
  params: Record<string, unknown>
  enabled: boolean
  created_at: string
  last_run_at: string | null
}

export interface CronJobCreate {
  job_type: string
  cron_expression: string
  params?: Record<string, unknown>
}

export const cron = {
  list: async (): Promise<CronJob[]> => {
    const res = await apiFetch('/api/cron')
    if (!res.ok) throw new Error('Failed to fetch cron jobs')
    return (await res.json()).jobs ?? []
  },

  create: async (data: CronJobCreate): Promise<CronJob> => {
    const res = await apiFetch('/api/cron', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    if (!res.ok) throw new Error('Failed to create cron job')
    return res.json()
  },

  delete: async (jobId: string): Promise<void> => {
    const res = await apiFetch(`/api/cron/${jobId}`, { method: 'DELETE' })
    if (!res.ok) throw new Error('Failed to delete cron job')
  },
}

// ─── Skills ───────────────────────────────────────────────────────────────────

export interface Skill {
  name: string
  description: string
  tools: string[]
  model_key: string
  source_path: string
}

export interface SkillInvokeResponse {
  skill: string
  response: string
}

export const skills = {
  list: async (): Promise<Skill[]> => {
    const res = await apiFetch('/api/skills')
    if (!res.ok) throw new Error('Failed to fetch skills')
    return (await res.json()).skills ?? []
  },

  invoke: async (
    name: string,
    message: string,
    sessionId?: string,
  ): Promise<SkillInvokeResponse> => {
    const body: Record<string, string> = { message }
    if (sessionId) body.session_id = sessionId
    const res = await apiFetch(`/api/skills/${name}/invoke`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!res.ok) throw new Error(`Failed to invoke skill '${name}'`)
    return res.json()
  },
}

// ─── Tokens ───────────────────────────────────────────────────────────────────

export interface TokenCountResult {
  input_tokens: number
  estimated_output_tokens: number
  total_estimated: number
  context_window: number
  exceeds_context_window: boolean
  headroom: number
  model_id: string
  method: string
  /** Token count where compression fires. ⚠ NOT SENT TODAY — don't assume SAFE_COMPRESSION_FRACTION × window. */
  compression_limit?: number
}

export const tokens = {
  count: async (message: string, modelId: string): Promise<TokenCountResult> => {
    const p = new URLSearchParams({ message, model_id: modelId })
    const res = await apiFetch(`/api/tokens/count?${p}`)
    if (!res.ok) throw new Error('Token count failed')
    return res.json()
  },
}

// ─── Context helpers ──────────────────────────────────────────────────────────
// Context (clientId, projectId) is managed by the settings store.
// Use getSettings() from '@/store/settings-store' for non-React contexts.
